import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTABanner from "../components/CTABanner";
import DealerJourney from "../components/dealer/DealerJourney";
import ExpandingPresence from "../components/dealer/ExpandingPresence";
import DealerRequirements from "../components/dealer/DealerRequirements";
import PartnershipCommitment from "../components/dealer/PartnershipCommitment";
import DealerFAQ from "../components/dealer/DealerFAQ";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  Award,
  TrendingUp,
  Megaphone,
  GraduationCap,
  LifeBuoy,
  HeartHandshake,
  CheckCircle2,
} from "lucide-react";
import Seo, { buildBreadcrumb } from "../components/Seo";
import { PAGE_SEO } from "../data/seo";
import PicImg from "../components/PicImg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  company: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  experience: "",
  current_business: "",
  investment: "",
  message: "",
};

const STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"];

export default function DealerPage() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/dealer-inquiry`, form);
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error("Submission failed. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const seo = PAGE_SEO.dealer;

  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        image={seo.image}
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Dealer", path: "/dealer" },
          ]),
        ]}
        jsonLdId="dealer"
      />
      <Navbar />

      {/* Hero — cinematic photo background, premium minimal layout */}
      <section
        data-testid="dealer-hero"
        className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden bg-liftile-black"
      >
        {/* Real project photograph background */}
        <div className="absolute inset-0">
          <PicImg
            src="/project-adani-korba.png"
            alt="LIFTILE machinery on a live construction project"
            className="w-full h-full object-cover scale-105"
          />
          {/* Cinematic dark overlay + subtle orange gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-liftile-black/85 via-liftile-black/70 to-liftile-black/95" />
          <div className="absolute inset-0 bg-gradient-to-tr from-liftile-orange/10 via-transparent to-transparent" />
          <div className="absolute inset-0 industrial-grid opacity-15" />
        </div>

        <div className="container-x relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="heading-eyebrow mb-5"
            >
              Become A Dealer
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="display-1 text-liftile-cream leading-[1.05]"
            >
              Become a <span className="text-liftile-orange">LIFTILE</span> Dealer
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="font-body text-base md:text-lg text-liftile-cream/75 mt-6 max-w-2xl leading-relaxed"
            >
              Partner with LIFTILE and grow your business with premium construction machinery,
              dependable engineering, trusted manufacturing and long-term business support.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 mt-10"
            >
              <a
                href="#apply"
                data-testid="dealer-hero-apply-btn"
                className="btn-primary group justify-center"
              >
                Apply for Dealership
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/contact"
                data-testid="dealer-hero-contact-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-[14px] border border-liftile-cream/30 text-liftile-cream font-heading text-xs uppercase tracking-[0.2em] hover:bg-liftile-cream hover:text-liftile-matte-black transition-all duration-300"
              >
                Contact Dealer Team
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.85, duration: 0.9 }}
              data-testid="dealer-hero-trust-statement"
              className="mt-10 max-w-xl font-body text-sm md:text-[15px] text-liftile-cream/55 italic leading-relaxed border-l-2 border-liftile-orange/60 pl-5"
            >
              Join a growing network built on engineering excellence, premium quality and
              long-term partnerships.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Why Partner With LIFTILE — 6 premium rounded feature cards */}
      <section
        data-testid="why-partner-section"
        className="bg-liftile-cream-light py-24 md:py-32 text-liftile-matte-black"
      >
        <div className="container-x">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="heading-eyebrow mb-3">Dealer Benefits</div>
            <h2 className="display-2">
              Why Partner With <span className="text-liftile-orange">LIFTILE</span>
            </h2>
            <p className="font-body text-base md:text-lg text-liftile-mid-gray/80 mt-5 leading-relaxed">
              Six reasons builders, distributors and channel partners choose LIFTILE as their
              long-term manufacturing partner.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-14 md:mt-16">
            {[
              {
                Icon: Award,
                title: "Premium Products",
                desc: "Manufactured using premium branded components and engineered for long-term reliability.",
              },
              {
                Icon: TrendingUp,
                title: "Attractive Dealer Opportunity",
                desc: "Build a profitable long-term business with a trusted manufacturing partner.",
              },
              {
                Icon: Megaphone,
                title: "Marketing Support",
                desc: "Professional brochures, digital marketing assets and branding support to help grow your business.",
              },
              {
                Icon: GraduationCap,
                title: "Technical Training",
                desc: "Product knowledge, installation guidance and technical assistance for your sales and service teams.",
              },
              {
                Icon: LifeBuoy,
                title: "After-Sales Support",
                desc: "Dedicated technical support, spare parts assistance and customer service after every sale.",
              },
              {
                Icon: HeartHandshake,
                title: "Long-Term Partnership",
                desc: "We believe in building lasting business relationships through trust, quality and continuous support.",
              },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                data-testid={`why-partner-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className="group relative h-full flex flex-col bg-white rounded-2xl p-8 md:p-9 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_40px_rgba(255,107,26,0.18)] border border-transparent hover:border-liftile-orange/40 hover:-translate-y-1 transition-all duration-500"
              >
                {/* Subtle top-right accent line on hover */}
                <span className="absolute top-0 right-0 h-[2px] w-0 bg-liftile-orange transition-all duration-500 group-hover:w-16 rounded-bl-full" />

                <div className="w-14 h-14 rounded-xl bg-liftile-orange/10 group-hover:bg-liftile-orange flex items-center justify-center transition-colors duration-500">
                  <b.Icon className="w-6 h-6 text-liftile-orange group-hover:text-white transition-colors duration-500" strokeWidth={1.6} />
                </div>

                <h3 className="font-heading text-base md:text-[17px] uppercase tracking-wider font-semibold mt-6 text-liftile-matte-black">
                  {b.title}
                </h3>

                <p className="font-body text-sm md:text-[15px] text-liftile-mid-gray/80 mt-3 leading-relaxed flex-1">
                  {b.desc}
                </p>

                <span className="block w-10 h-[2px] bg-liftile-orange/30 mt-7 group-hover:w-20 group-hover:bg-liftile-orange transition-all duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 — Dealer Journey (6-step horizontal timeline) */}
      <DealerJourney />

      {/* Section 4 — Expanding Our Presence (identical map as About/Projects) */}
      <ExpandingPresence />

      {/* Section 5 — Dealer Requirements (4 premium cards) */}
      <DealerRequirements />

      {/* Section 6 — Dealer Application Form (premium cream + white card) */}
      <section
        id="apply"
        data-testid="dealer-application-form"
        className="relative bg-liftile-cream-light py-24 md:py-32 overflow-hidden"
      >
        {/* Soft orange ambient accent */}
        <div
          aria-hidden
          className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full pointer-events-none opacity-[0.16]"
          style={{
            background:
              "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
            filter: "blur(40px)",
          }}
        />

        <div className="container-x relative">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="heading-eyebrow mb-3">Dealer Application</div>
            <h2
              data-testid="dealer-form-heading"
              className="display-2 text-liftile-matte-black"
            >
              Apply for <span className="text-liftile-orange">LIFTILE Dealership</span>
            </h2>
            <p className="font-body text-base md:text-lg text-liftile-matte-black/70 mt-6 leading-relaxed">
              Interested in becoming a LIFTILE dealer? Complete the enquiry form
              below and our business development team will contact you shortly.
            </p>
          </motion.div>

          {/* Form Card or Success Card */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl mx-auto mt-14 md:mt-16"
          >
            {submitted ? (
              <SuccessCard onReset={() => setSubmitted(false)} />
            ) : (
              <form
                onSubmit={submit}
                data-testid="dealer-form"
                className="bg-white rounded-2xl p-8 md:p-12 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.10)] border border-liftile-matte-black/[0.05] space-y-6"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
                  <Field label="Full Name" required testId="dealer-name" value={form.name} onChange={update("name")} />
                  <Field label="Company Name" required testId="dealer-company" value={form.company} onChange={update("company")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
                  <Field label="Mobile Number" required type="tel" testId="dealer-phone" value={form.phone} onChange={update("phone")} placeholder="+91" />
                  <Field label="Email Address" required type="email" testId="dealer-email" value={form.email} onChange={update("email")} placeholder="you@company.com" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
                  <Field label="City" required testId="dealer-city" value={form.city} onChange={update("city")} />
                  <SelectField label="State" required testId="dealer-state" value={form.state} onChange={update("state")} options={STATES} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
                  <Field label="Business Experience" required testId="dealer-experience" value={form.experience} onChange={update("experience")} placeholder="e.g. 5 years" />
                  <Field label="Annual Business Turnover (Optional)" testId="dealer-investment" value={form.investment} onChange={update("investment")} placeholder="e.g. ₹50 Lakhs" />
                </div>
                <Textarea label="Current Business Profile" required testId="dealer-current-business" value={form.current_business} onChange={update("current_business")} rows={3} placeholder="Briefly describe your current business, products dealt with and target customer segment." />
                <Textarea label="Additional Message" testId="dealer-message" value={form.message} onChange={update("message")} rows={4} placeholder="Anything else you'd like our team to know..." />

                <div className="pt-2">
                  <button
                    type="submit"
                    data-testid="dealer-submit-btn"
                    disabled={loading}
                    className="group relative inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-10 py-[18px] rounded-xl overflow-hidden transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-liftile-orange to-liftile-orange-dark opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                    <span className="relative">
                      {loading ? "Submitting Application..." : "Submit Dealer Application"}
                    </span>
                    {!loading && (
                      <ArrowRight className="w-4 h-4 relative transition-transform duration-300 group-hover:translate-x-1" />
                    )}
                  </button>

                  <p className="font-body text-xs md:text-sm text-liftile-matte-black/55 mt-4 leading-relaxed">
                    By submitting, you agree to be contacted by the LIFTILE
                    business development team regarding your dealership enquiry.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Section 7 — Dealer FAQ */}
      <DealerFAQ />

      {/* Section 8 — Our Partnership Commitment */}
      <PartnershipCommitment />

      {/* Section 9 — Final CTA */}
      <CTABanner
        eyebrow="Join the Network"
        headline="Let's Grow Together"
        description="Become part of the growing LIFTILE dealer network and build a successful long-term business with a trusted construction machinery manufacturer."
        bgImage="/project-iocl-vadodara.png"
        primaryLabel="Apply for Dealership"
        primaryHref="#apply"
        secondaryLabel="Contact Dealer Team"
        secondaryHref="/contact"
      />

      <Footer />
    </>
  );
}

/* ---------- Premium form field helpers ---------- */
function Field({ label, required, testId, type = "text", placeholder, value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] font-heading text-liftile-matte-black/65 mb-2.5 font-semibold">
        {label} {required && <span className="text-liftile-orange">*</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-liftile-cream-light/50 border border-liftile-matte-black/10 rounded-xl px-5 py-3.5 font-body text-[15px] text-liftile-matte-black placeholder:text-liftile-matte-black/30 focus:outline-none focus:border-liftile-orange focus:bg-white focus:ring-4 focus:ring-liftile-orange/10 transition-all duration-300"
      />
    </div>
  );
}

function SelectField({ label, required, testId, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] font-heading text-liftile-matte-black/65 mb-2.5 font-semibold">
        {label} {required && <span className="text-liftile-orange">*</span>}
      </label>
      <select
        data-testid={testId}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full bg-liftile-cream-light/50 border border-liftile-matte-black/10 rounded-xl px-5 py-3.5 font-body text-[15px] text-liftile-matte-black focus:outline-none focus:border-liftile-orange focus:bg-white focus:ring-4 focus:ring-liftile-orange/10 transition-all duration-300 appearance-none bg-[length:14px] bg-[position:right_1.25rem_center] bg-no-repeat"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23FF6B1A' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
        }}
      >
        <option value="">Select State</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function Textarea({ label, required, testId, value, onChange, rows = 4, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] font-heading text-liftile-matte-black/65 mb-2.5 font-semibold">
        {label} {required && <span className="text-liftile-orange">*</span>}
      </label>
      <textarea
        data-testid={testId}
        required={required}
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-liftile-cream-light/50 border border-liftile-matte-black/10 rounded-xl px-5 py-3.5 font-body text-[15px] text-liftile-matte-black placeholder:text-liftile-matte-black/30 focus:outline-none focus:border-liftile-orange focus:bg-white focus:ring-4 focus:ring-liftile-orange/10 transition-all duration-300 resize-none"
      />
    </div>
  );
}

function SuccessCard({ onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      data-testid="dealer-form-success"
      className="bg-white rounded-2xl p-10 md:p-14 shadow-[0_18px_45px_-12px_rgba(255,107,26,0.28)] border border-liftile-orange/30 text-center"
    >
      <div className="mx-auto w-16 h-16 rounded-full bg-liftile-orange/10 flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-liftile-orange" strokeWidth={1.8} />
      </div>
      <h3 className="font-display uppercase text-liftile-matte-black tracking-tight text-2xl sm:text-3xl md:text-[2.25rem] mt-7 leading-tight">
        Thank You for Your{" "}
        <span className="text-liftile-orange">Interest</span>
      </h3>
      <p className="font-body text-liftile-matte-black/70 text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed">
        Thank you for your interest in becoming a LIFTILE Dealer. Our business
        development team will review your enquiry and contact you shortly.
      </p>
      <div className="mx-auto w-14 h-px bg-liftile-orange mt-9" />
      <button
        type="button"
        onClick={onReset}
        data-testid="dealer-form-reset"
        className="mt-9 inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.2em] text-liftile-matte-black/70 hover:text-liftile-orange transition-colors"
      >
        Submit another enquiry
        <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
}
